// The per-run agent-event channel, carved from
// `src/features/agent-harness/agent-events/use-agent-event-stream.ts` (now a
// delegate). Same story as its `use-agent-stream.ts` sibling and the same two
// severances: `EventDedup` is in-package now, and `eventsPath`/`streamHeaders`
// are REQUIRED parameters rather than defaults onto the app-side transport
// assembly. These two hooks were the entire measured leak of the user-wide event
// mechanism into run scope, which is why they move together.
import { fetchEventSource } from '@fortaine/fetch-event-source';
import { useEffect, useRef, useState } from 'react';

import { logger } from '@heidi/runtime/logger';
import { useLatestRef } from '@heidi/utils';

import type { AgentEvent, StreamHeadersFactory } from '../../../contracts';
import { EventDedup } from '../../events/event-dedup';
import {
  isStableConnection,
  refreshAuthAfterUnauthorized,
  withClientCapabilities,
} from '../stream/reconnect';
import { decideStreamRetry } from '../stream/retry-policy';
import { responseError, sleep, StreamHttpError } from '../stream/stream-http';
import { parseAgentEvent } from './parse-agent-event';

export type AgentEventStreamStatus =
  | 'idle'
  | 'connecting'
  | 'streaming'
  | 'reconnecting'
  | 'done'
  | 'error';

export interface AgentEventStreamState {
  status: AgentEventStreamStatus;
  endedReason: 'closed' | 'failed' | null;
  error: Error | null;
  lastEventId: string | null;
}

export interface UseAgentEventStreamOptions {
  agentRunId: string | null | undefined;
  enabled: boolean;
  /** REQUIRED — see the `use-agent-stream.ts` sibling for why. */
  eventsPath: (agentRunId: string) => string;
  streamHeaders: StreamHeadersFactory;
  lastEventId?: string | null;
  onEvent: (event: AgentEvent) => void;
}

export const useAgentEventStream = ({
  agentRunId,
  enabled,
  eventsPath,
  streamHeaders: headersFactory,
  lastEventId,
  onEvent,
}: UseAgentEventStreamOptions): AgentEventStreamState => {
  const [status, setStatus] = useState<AgentEventStreamStatus>('idle');
  const [endedReason, setEndedReason] = useState<'closed' | 'failed' | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [deliveredLastEventId, setDeliveredLastEventId] = useState<string | null>(
    lastEventId ?? null
  );
  const cursorRef = useRef<string | null>(lastEventId ?? null);
  const dedupRef = useRef(new EventDedup(128));
  const onEventRef = useLatestRef(onEvent);

  useEffect(() => {
    cursorRef.current = lastEventId ?? null;
    setDeliveredLastEventId(lastEventId ?? null);
    dedupRef.current = new EventDedup(128);
  }, [agentRunId, lastEventId]);

  useEffect(() => {
    if (!enabled || !agentRunId) {
      setStatus('idle');
      setEndedReason(null);
      setError(null);
      return;
    }

    const ctrl = new AbortController();
    let attempt = 0;

    const run = async () => {
      let authRefreshed = false;
      while (!ctrl.signal.aborted) {
        let sawChannelEnded = false;
        let openedAt: number | null = null;
        try {
          setStatus(attempt === 0 ? 'connecting' : 'reconnecting');
          setError(null);
          await fetchEventSource(withClientCapabilities(eventsPath(agentRunId)), {
            method: 'GET',
            headers: headersFactory({ lastEventId: cursorRef.current }),
            signal: ctrl.signal,
            openWhenHidden: true,
            onopen: async (response) => {
              if (!response.ok) {
                throw new StreamHttpError(response.status, await responseError(response));
              }
              openedAt = Date.now();
              authRefreshed = false;
              setStatus('streaming');
            },
            onmessage: (message) => {
              if (message.id) {
                cursorRef.current = message.id;
                setDeliveredLastEventId(message.id);
              }
              const event = parseAgentEvent(message.data);
              if (!event) return;
              if (!dedupRef.current.markSeen(event.event_id)) return;

              attempt = 0;
              onEventRef.current(event);
              if (event.type === 'channel.ended') {
                sawChannelEnded = true;
                const reason =
                  event.payload &&
                  typeof event.payload === 'object' &&
                  'reason' in event.payload &&
                  event.payload.reason === 'failed'
                    ? 'failed'
                    : 'closed';
                setEndedReason(reason);
                setStatus('done');
                ctrl.abort();
              }
            },
            onerror: (nextError) => {
              throw nextError instanceof Error ? nextError : new Error(String(nextError));
            },
          });

          if (ctrl.signal.aborted) return;
          if (sawChannelEnded) {
            setStatus('done');
            return;
          }
          throw new Error('Agent-event stream closed before channel.ended');
        } catch (nextError) {
          if (ctrl.signal.aborted) return;
          if (isStableConnection(openedAt)) attempt = 0;
          const refreshed =
            !authRefreshed && (await refreshAuthAfterUnauthorized(nextError));
          if (ctrl.signal.aborted) return;
          if (refreshed) authRefreshed = true;
          const decision = decideStreamRetry(nextError, attempt, refreshed);
          if (decision.type === 'stop') {
            setError(decision.error);
            setStatus('error');
            return;
          }
          setError(decision.error);
          logger.warn('[agent-harness] Agent-event stream interrupted; reconnecting', {
            agent_run_id: agentRunId,
            attempt,
            error: decision.error.message,
          });
          attempt = decision.nextAttempt;
          await sleep(decision.delayMs, ctrl.signal);
        }
      }
    };

    void run();

    return () => {
      ctrl.abort();
    };
  }, [agentRunId, enabled, eventsPath, headersFactory, onEventRef]);

  return { status, endedReason, error, lastEventId: deliveredLastEventId };
};
